import { useEffect, useReducer, useState, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { Box, Grid2 as Grid } from "@mui/material"
import { compact, findIndex, size, isEqual, set, get, reduce } from "lodash"
import { InfoOutlined } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"
import { getRealValue } from "components/factor-constraint-input/helpers"
import { dollarFormatter, assetDollarFormatter } from "helpers/formatters/numbers"
import { KEY_CODES } from "helpers/keyboard"
import { CONTRIBUTOR_WITH_ADV } from "helpers/shapes/simulator"
import OriginalRow from "./original-row"
import { FIELDS } from "helpers/simulator"
import { safeDivide } from "helpers/math"
import { pruneValue, numberFormatter } from "modules/rebalance/helpers"
import InputRows from "./input-rows"

const EQUIVALENT_FIELDS = [
  { path: FIELDS.economicExposure, format: assetDollarFormatter },
  { path: FIELDS.positionPercentEquity, format: numberFormatter },
  { path: FIELDS.activePercentEquity, format: numberFormatter },
  { path: ["delta", FIELDS.economicExposure], format: assetDollarFormatter },
  { path: ["delta", FIELDS.positionPercentEquity], format: numberFormatter },
  { path: ["delta", FIELDS.activePercentEquity], format: numberFormatter },
]

export default function InputWidget({ selectedPosition, isActive, setValue, field, setField, addTrade, workingEquity, tradeDisabled, isActiveTradingDisabled, hideEconomicExposure }) {
  const { basePercentEquity, averageDailyVolume, type, activePercentEquity, isInPositionSet } = selectedPosition ?? {}
  // Not held positions don't have a type, so we default to "security"
  const positionType = type || "security"
  // We don't need to format this is a percentage, as that already happens upstream
  const basePercentEquityTooltip = basePercentEquity ? `Benchmark: ${numberFormatter(basePercentEquity)}%` : `This ${positionType} is not part of the benchmark.`

  const fields = useMemo(() => (
    compact([!hideEconomicExposure && FIELDS.economicExposure, FIELDS.positionPercentEquity, isActive && FIELDS.activePercentEquity])
  ), [hideEconomicExposure, isActive])

  const initialSelectedInputColumn = useMemo(
    () => (field === FIELDS.activePercentEquity && isActiveTradingDisabled ? 0 : findIndex(fields, f => f === field)),
    [field, fields, isActiveTradingDisabled],
  )

  // selectedInput is [row, column]
  const [selectedInput, setSelectedInput] = useState([0, initialSelectedInputColumn])
  const { original } = selectedPosition

  // Keep the selected input column in sync when the requested field changes
  useEffect(() => {
    if (initialSelectedInputColumn >= 0) {
      setSelectedInput(prev => [prev[0], initialSelectedInputColumn])
    }
  }, [initialSelectedInputColumn])

  const initialState = useMemo(() => {
    const { economicExposure, positionPercentEquity, activePercentEquity } = original ?? {}
    const state = {
      economicExposure: 0,
      positionPercentEquity: 0,
      activePercentEquity: 0,
      delta: {
        economicExposure: 0,
        positionPercentEquity: 0,
        activePercentEquity: 0,
      },
    }
    if (isInPositionSet) {
      return {
        ...state,
        economicExposure: assetDollarFormatter(selectedPosition.economicExposure),
        positionPercentEquity: numberFormatter(selectedPosition.positionPercentEquity),
        activePercentEquity: numberFormatter(selectedPosition.activePercentEquity),
        delta: {
          economicExposure: assetDollarFormatter(getRealValue(selectedPosition.economicExposure) - getRealValue(economicExposure)),
          positionPercentEquity: numberFormatter(selectedPosition.positionPercentEquity - positionPercentEquity),
          activePercentEquity: numberFormatter(selectedPosition.activePercentEquity - activePercentEquity),
        },
      }
    }
    return state
  }, [isInPositionSet, original, selectedPosition])

  const getPercentEquity = useCallback((field, value, delta) => {
    if (!delta) {
      switch (field) {
        case "economicExposure":
          return safeDivide(value, workingEquity) * 100
        case "positionPercentEquity":
          return value
        case "activePercentEquity":
          return (value + basePercentEquity)
      }
    }
    switch (field) {
      case "economicExposure":
        return safeDivide(original[FIELDS.economicExposure] + value, workingEquity) * 100
      case "positionPercentEquity":
        return (original[FIELDS.positionPercentEquity] + value)
      case "activePercentEquity":
        return (original[FIELDS.activePercentEquity] + value + basePercentEquity)
    }
  }, [basePercentEquity, original, workingEquity])

  const computeEquivalentFields = useCallback((value, field, delta) => {
    const realValue = getRealValue(value)
    const positionPercentEquity = getPercentEquity(field, +realValue, delta)
    let computedValues = {
      [FIELDS.economicExposure]: positionPercentEquity / 100 * workingEquity,
      [FIELDS.positionPercentEquity]: positionPercentEquity,
      [FIELDS.activePercentEquity]: positionPercentEquity - basePercentEquity,
    }
    computedValues = {
      ...computedValues,
      delta: {
        [FIELDS.economicExposure]: computedValues[FIELDS.economicExposure] - get(original, FIELDS.economicExposure, 0),
        [FIELDS.positionPercentEquity]: computedValues[FIELDS.positionPercentEquity] - get(original, FIELDS.positionPercentEquity, 0),
        [FIELDS.activePercentEquity]: computedValues[FIELDS.activePercentEquity] - get(original, FIELDS.activePercentEquity, 0),
      },
    }

    return reduce(EQUIVALENT_FIELDS, (acc, { path, format }) => set(acc, path, format(get(computedValues, path))), {})
  }, [getPercentEquity, workingEquity, basePercentEquity, original])

  const [values, setLocalValue] = useReducer((state, action) => {
    const { field, value, type } = action
    const trimmedValue = pruneValue(value, field === "economicExposure")
    if (field) {
      const newState = computeEquivalentFields(trimmedValue, field, type === "delta")
      return set(newState, compact([type === "delta" && type, field]), trimmedValue)
    }
    return initialState
  }, initialState)

  useEffect(() => {
    if (selectedPosition) {
      setLocalValue({})
      // Reset focused row when position changes
      setSelectedInput(prev => prev[0] !== 0 ? [0, prev[1]] : prev)
    }
  }, [selectedPosition])

  const getValue = useCallback((type, field) => {
    return type === "delta" ? get(values, ["delta", field]) : get(values, field)
  }, [values])

  const computeValue = useCallback((type, field) => {
    let initialValue
    const value = getValue(type, field)
    if (type !== "delta") {
      return field === FIELDS.economicExposure ? getRealValue(value) : value
    }

    if (field === FIELDS.economicExposure) {
      initialValue = getRealValue(original.economicExposure)
      return +initialValue + +getRealValue(value)
    }

    initialValue = parseFloat(get(original, field, 0))
    return initialValue + parseFloat(value)
  }, [getValue, original])

  useEffect(() => {
    if (!isInPositionSet && activePercentEquity) {
      setLocalValue({ field: FIELDS.activePercentEquity, value: numberFormatter(activePercentEquity) })
    }
  }, [activePercentEquity, isInPositionSet])

  // Keep the values between this component and the parent component in sync
  useEffect(() => {
    const [row, col] = selectedInput
    const field = fields[col]
    if (!field) return
    const type = row === 1 ? "delta" : "rebalanced"
    const value = computeValue(type, field)
    setValue(value)
  }, [values, fields, selectedInput, setValue, computeValue])

  const isCurrentInput = useCallback((row, col) => {
    return isEqual([row, col], selectedInput)
  }, [selectedInput])

  const onChange = useCallback((field, type) => {
    return (e) => {
      const value = e.target.value
      setLocalValue({ field, value, type })
      setValue(value)
    }
  }, [setLocalValue, setValue])

  const onFocus = useCallback((row, col, field, type) => {
    return () => {
      if (isCurrentInput(row, col)) { return }
      setSelectedInput([row, col])
      setValue(computeValue(type, field))
      setField(field)
    }
  }, [isCurrentInput, setSelectedInput, setValue, computeValue, setField])

  const onKeyDown = useCallback((e) => {
    switch (e.keyCode) {
      case KEY_CODES.ENTER:
        if (!tradeDisabled) {
          addTrade()
        }
        break
      case KEY_CODES.ESCAPE:
        setLocalValue({})
        break
    }
  }, [tradeDisabled, addTrade, setLocalValue])

  const calculateDaysToTrade = useCallback(() => {
    let initialValue, tradeAmount
    const usingDelta = get(selectedInput, 0) === 1
    if (!usingDelta) {
      switch (field) {
        case FIELDS.economicExposure:
          initialValue = getRealValue(get(initialState, field))
          tradeAmount = initialValue - getRealValue(get(values, field))
          break
        case FIELDS.positionPercentEquity:
        case FIELDS.activePercentEquity:
          initialValue = parseFloat(get(initialState, field))
          tradeAmount = (initialValue - parseFloat(get(values, field))) * workingEquity / 100
          break
      }
    } else {
      tradeAmount = getValue("delta", field)
      if (field === FIELDS.economicExposure) {
        tradeAmount = getRealValue(tradeAmount)
      } else {
        tradeAmount = parseFloat(tradeAmount) * workingEquity / 100
      }
    }
    return numberFormatter(Math.abs(tradeAmount) / averageDailyVolume)
  }, [selectedInput, field, initialState, values, workingEquity, getValue, averageDailyVolume])

  const daysToTrade = calculateDaysToTrade()
  return (
    <Grid container className="input-widget">
      <Box className="content-wrapper">
        <Box className="content">
          <div className="grid-container" data-columns={size(fields)}>
            <div className="grid-item"> </div>
            {!hideEconomicExposure && <div className="grid-item top-label">Total position size</div>}
            <div className="grid-item top-label">Total % equity</div>
            {isActive && <div className="grid-item top-label">
              <OPTooltip title={basePercentEquityTooltip}><InfoOutlined color="primary" /></OPTooltip>
              <span>Total active % equity</span>
            </div>}
            {original && <OriginalRow {...{ fields, original }} />}
            <InputRows
              {...{
                original,
                fields,
                isCurrentInput,
                onFocus,
                isActiveTradingDisabled,
                getValue,
                onChange,
                onKeyDown,
              }}
            />
          </div>
        </Box>
        <Box className="footer-info">
          {daysToTrade > 0.05 && <>
            <i>Days to trade: {daysToTrade}</i> - {dollarFormatter(averageDailyVolume)} ADV
          </>}
        </Box>
      </Box>
    </Grid>
  )
}
InputWidget.propTypes = {
  selectedPosition: CONTRIBUTOR_WITH_ADV,
  isActive: PropTypes.bool,
  isActiveTradingDisabled: PropTypes.bool,
  workingEquity: PropTypes.number,
  field: PropTypes.string,
  tradeDisabled: PropTypes.bool,
  setValue: PropTypes.func,
  setField: PropTypes.func,
  addTrade: PropTypes.func,
  hideEconomicExposure: PropTypes.bool,
}
